import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, CATEGORIES } from "@/lib/api";
import { Clock, Users, Tag } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { fmtTime12, fmtDateLong } from "@/lib/api";

export default function Workshops() {
  const c = useContent().pages.workshops;
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/workshops", { params: cat !== "all" ? { category: cat } : {} }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [cat]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
      <div className="max-w-3xl">
        <div className="text-terracotta text-sm tracking-widest uppercase font-medium">{c.eyebrow}</div>
        <h1 className="mt-2 font-serif font-bold text-5xl md:text-6xl leading-tight">{c.heading}</h1>
        <p className="mt-4 text-lg text-charcoal/70">{c.description}</p>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="two-paths">
        <div className="rounded-3xl bg-white shadow-card p-5 border-2 border-terracotta/40" data-testid="path-book">
          <div className="text-terracotta text-xs tracking-widest uppercase font-bold">Path 1 · Book now</div>
          <div className="mt-1 font-serif text-xl">Join a scheduled workshop</div>
          <p className="mt-1 text-sm text-charcoal/70">Pick one of the open dates below, choose your options, and pay online. Your spot is confirmed instantly.</p>
        </div>
        <div className="rounded-3xl bg-charcoal text-cream shadow-card p-5" data-testid="path-request">
          <div className="text-forest-light text-xs tracking-widest uppercase font-bold">Path 2 · Request a date</div>
          <div className="mt-1 font-serif text-xl">Private party or corporate event</div>
          <p className="mt-1 text-sm text-cream/75">Want your own date for a group, team, or celebration? Send a request and we'll confirm details and pricing with you.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/private-events" data-testid="path-request-private" className="rounded-full bg-terracotta text-white text-xs font-bold px-4 py-2">Request a private event</Link>
            <Link to="/corporate" data-testid="path-request-corporate" className="rounded-full bg-white/10 text-cream text-xs font-bold px-4 py-2">Request a corporate event</Link>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="workshop-filter-chips">
        {CATEGORIES.map((c) => (
          <button key={c.id} onClick={() => setCat(c.id)} data-testid={`filter-${c.id}`}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${cat === c.id ? "bg-terracotta text-white" : "bg-white text-charcoal hover:bg-cream-alt"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-16 text-center text-charcoal/50">Loading workshops…</div>
      ) : items.length === 0 ? (
        <div className="mt-16 text-center bg-white rounded-3xl p-16 shadow-card">
          <h3 className="font-serif text-2xl">No workshops in this category right now.</h3>
          <p className="mt-2 text-charcoal/70">Check back soon — we add new dates weekly.</p>
        </div>
      ) : (
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((w) => (
            <div key={w.id} className="bg-white rounded-3xl overflow-hidden shadow-card hover:shadow-hover transition-transform hover:-translate-y-1" data-testid={`workshop-card-${w.id}`}>
              {w.photo_url && <img src={w.photo_url} alt={w.name} className="h-52 w-full object-cover"/>}
              <div className="p-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-cream-alt text-charcoal text-xs">{w.category.replace(/_/g, ' ')}</span>
                  {w.featured && <span className="px-3 py-1 rounded-full bg-ochre text-charcoal text-xs">Featured</span>}
                  {w.sold_out || w.spots_remaining === 0 ? (
                    <span className="px-3 py-1 rounded-full bg-charcoal text-white text-xs">Sold Out</span>
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-forest/10 text-forest text-xs font-semibold">Available</span>
                  )}
                </div>
                <h3 className="mt-4 font-serif text-2xl font-semibold">{w.name}</h3>
                <p className="mt-2 text-sm text-charcoal/70 line-clamp-2">{w.description}</p>
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-charcoal-muted">
                  <div className="flex items-center gap-1.5"><Clock size={14}/> {w.multiple_dates ? "Multiple dates available" : <>{fmtDateLong(w.date, { month: 'short', day: 'numeric' })} · {fmtTime12(w.start_time)}</>}</div>
                  <div className="flex items-center gap-1.5"><Users size={14}/> Ages {w.age_requirement}</div>
                  <div className="flex items-center gap-1.5"><Tag size={14}/> {w.duration_minutes} min</div>
                  <div className="flex items-center gap-1.5 font-serif text-lg font-semibold" data-testid={`ws-price-${w.id}`}>
                    {w.sale_active && w.sale_price != null && Number(w.sale_price) < Number(w.price) ? (
                      <><span className="text-terracotta">${w.sale_price}</span><span className="line-through text-charcoal-muted text-sm">${w.price}</span></>
                    ) : (
                      <span className="text-terracotta">${w.effective_price ?? w.price}</span>
                    )}
                  </div>
                </div>
                <Link to={`/workshops/${w.id}`} data-testid={`book-workshop-btn-${w.id}`}
                  className={`mt-5 block text-center rounded-full px-5 py-3 font-semibold transition-transform hover:-translate-y-0.5 ${w.sold_out || w.spots_remaining === 0 ? "bg-cream-alt text-charcoal-muted cursor-not-allowed pointer-events-none" : "bg-terracotta text-white shadow-button"}`}>
                  {w.sold_out || w.spots_remaining === 0 ? "Sold Out" : "Book Now"}
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
